'use client'

import { useAtomValue } from 'jotai'
import { ArrowLeft, X } from 'lucide-react'
import { motion } from 'motion/react'
import { Button } from '@/src/components/ui/button'

import { preferredAvatarAtom } from '@/src/stores/accounts/atoms'
import {
  usePortfolioDrawer,
  usePortfolioView,
} from '@/src/stores/portfolio-drawer/actions'
import { userAtom } from '@/src/stores/user/atoms'

import { AccountButton } from '../accounts/accounts-button'

import { ReferralCodeDialog } from './dialogs/referral-code'
import { MpcDetails } from './mpc-screen'

import { Account } from './views/account'
import { EditAccountEmail } from './views/edit-account-email'
import { EditAccountName } from './views/edit-account-name'
import { EditWalletName } from './views/edit-wallet-name'
import { IgnoredWalletsScreen } from './views/ignored-wallets'
import { ManageAccount } from './views/manage-account'
import { ManageWallet } from './views/manage-wallet'
import { Overview } from './views/overview'
import { ReferralsScreen } from './views/referrals'
import { SettingsScreen } from './views/settings'
import { VerifyAccountEmail } from './views/verify-account-email'
import { Wallets } from './views/wallets'

const MotionButton = motion.create(Button)

const titles: Record<string, string> = {
  'manage-account': 'Account',
  'edit-account-name': 'Display name',
  'edit-account-email': 'Update email',
  'verify-email': 'Verify email',
  'edit-wallet-name': 'Wallet name',
  'manage-wallet': 'Manage wallet',
  settings: 'Settings',
  'settings-autopilot': 'Autopilot recovery settings',
  'settings-ignored-accounts': 'Ignored wallets',
}

export const PortfolioScreen = () => {
  const { close: closePortfolioDrawer } = usePortfolioDrawer()

  const { currentView, updateView, goBack } = usePortfolioView()
  const user = useAtomValue(userAtom)
  const avatar = useAtomValue(preferredAvatarAtom)

  return (
    <div className='relative group'>
      <nav className='flex items-center justify-between h-navbar px-page-gutter sticky top-0 z-20 bg-gradient-to-b from-background to-transparent'>
        {currentView.view === 'overview' && user ? (
          <AccountButton
            user={user}
            onClick={() => updateView({ view: 'account' })}
            avatar={avatar}
            imageLayoutId='portfolio-avatar'
          />
        ) : (
          <MotionButton
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            type='button'
            onClick={goBack}
          >
            <ArrowLeft size={24} className='text-link' />
          </MotionButton>
        )}
        <span className='xl:text-xl font-semibold text-primary'>
          {titles[currentView.view]}
        </span>
        {currentView.view === 'referrals' ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ReferralCodeDialog />
          </motion.div>
        ) : (
          <Button type='button' onClick={closePortfolioDrawer}>
            <X />
          </Button>
        )}
      </nav>
      <div className='px-page-gutter'>
        <PortfolioDrawerView />
      </div>
    </div>
  )
}

const PortfolioDrawerView = () => {
  const { currentView } = usePortfolioView()

  switch (currentView.view) {
    case 'overview':
      return <Overview />
    case 'account':
      return <Account />
    case 'manage-account':
      return <ManageAccount />
    case 'edit-account-name':
      return <EditAccountName />
    case 'edit-account-email':
      return <EditAccountEmail />
    case 'verify-email':
      return <VerifyAccountEmail />
    case 'wallets':
      return <Wallets />
    case 'manage-wallet':
      return <ManageWallet />
    case 'edit-wallet-name':
      return <EditWalletName />
    case 'settings':
      return <SettingsScreen />
    case 'settings-autopilot':
      return <MpcDetails />
    case 'settings-ignored-accounts':
      return <IgnoredWalletsScreen />
    case 'referrals':
      return <ReferralsScreen />
    default:
      return null
  }
}
